import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { defaultOrder, Order } from "../../models/responses/order";
import { Article } from "../../models/responses/article";
import { defaultOrderUser, OrderUser } from "../../models/responses/orderUser";
import { QueryOrder } from "../../models/responses/queryOrder";
import { interpretAsDecimal } from "../services/calculationsHelperService";
import { OrderHistory } from "../../models/responses/orderHistory";
import { EmailHistory } from "../../models/responses/emailHistory";

export interface OrderState {
    orderList: Order[];
    order: Order;
    pdfVerification: Blob | undefined;
    orderUser: OrderUser;
    ordersByUserId: Order[];
    exportedNotImportedOrders: Order[];
    isWaitingOrderPostResponse: boolean;
    isWaitingOrderPutResponse: boolean;
    isWaitingOrderGetResponse: boolean;
    isWaitingExportedNotImportedOrdersGetResponse: boolean;
    importedArticlesCount: number;
    queryOrderList: Order[] | null;
    totalQueryOrder: number;
    isWaitingOrderExportResponse: boolean;
    isWaitingRevertOrderExportResponse: boolean;
    isWaitingOrderHistoryGetResponse: boolean;
    isWaitingStockResponse: boolean;
    emailHistory: EmailHistory[] | null;
    isWaitingSendOrderEmailResponse: boolean;
    hasUpdatedStock: boolean;
    isWaitingPriceProposalPreviewResponse: boolean;
    isWaitingOrderStatusSyncResponse: boolean;
}

const initialState: OrderState = {
    orderList: [],
    order: { ...defaultOrder },
    orderUser: { ...defaultOrderUser },
    ordersByUserId: [],
    exportedNotImportedOrders: [],
    isWaitingOrderPostResponse: false,
    isWaitingOrderPutResponse: false,
    isWaitingOrderGetResponse: false,
    isWaitingExportedNotImportedOrdersGetResponse: false,
    importedArticlesCount: 0,
    queryOrderList: null,
    totalQueryOrder: 0,
    isWaitingOrderExportResponse: false,
    isWaitingRevertOrderExportResponse: false,
    isWaitingOrderHistoryGetResponse: false,
    isWaitingStockResponse: false,
    pdfVerification: undefined,
    emailHistory: null,
    isWaitingSendOrderEmailResponse: false,
    hasUpdatedStock: false,
    isWaitingPriceProposalPreviewResponse: false,
    isWaitingOrderStatusSyncResponse: false
};

const calculateArticleTotals = (articles: Article[] | null): Article[] | null => {
    if (!articles)
        return null;

    return articles.map(article => ({
        ...article,
        salesPrice: parseFloat(interpretAsDecimal(parseFloat(article.salesPrice)).toFixed(2)).toString(),
        workshopSalesPrice: parseFloat(interpretAsDecimal(parseFloat(article.workshopSalesPrice)).toFixed(2)).toString(),
        storeSalesPrice: parseFloat(interpretAsDecimal(parseFloat(article.storeSalesPrice)).toFixed(2)).toString(),
        purchasePrice: parseFloat(interpretAsDecimal(parseFloat(article.purchasePrice)).toFixed(2)).toString(),
        quantity: parseFloat(interpretAsDecimal(article.quantity).toFixed(2)),
        vat: parseFloat(interpretAsDecimal(article.vat).toFixed(2)),
        deliveredQuantity: parseFloat(interpretAsDecimal(article.deliveredQuantity).toFixed(2)),
        totalExVat: parseFloat((interpretAsDecimal(parseFloat(article.salesPrice)) * parseFloat(interpretAsDecimal(article.quantity).toFixed(2))).toFixed(2)),
        totalIncVat: parseFloat((interpretAsDecimal(parseFloat(article.salesPrice)) * parseFloat(interpretAsDecimal(article.quantity).toFixed(2)) + parseFloat(interpretAsDecimal(article.vat).toFixed(2))).toFixed(2))
    }));
};

const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        setOrderList: (state, action: PayloadAction<{ orders: Order[] }>) => {
            state.orderList = action.payload.orders.map(order => ({
                ...order,
                articles: calculateArticleTotals(order.articles)
            }));
        },
        setOrder: (state, action: PayloadAction<{ order: Order }>) => {
            state.order = {
                ...action.payload.order,
                articles: calculateArticleTotals(action.payload.order.articles)
            };
        },
        setOrderUser: (state, action: PayloadAction<{ orderUser: OrderUser }>) => {
            state.orderUser = {
                ...action.payload.orderUser
            };
        },
        setOrderListByUserId: (state, action: PayloadAction<{ orders: Order[] }>) => {
            state.ordersByUserId = action.payload.orders.map(order => ({
                ...order,
                articles: calculateArticleTotals(order.articles)
            }));
        },
        setExportedNotImportedOrders: (state, action: PayloadAction<{ orders: Order[] }>) => {
            state.exportedNotImportedOrders = action.payload.orders.map(order => ({
                ...order
            }));
        },
        setIsWaitingOrderPostResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingOrderPostResponse = action.payload;
        },
        setIsWaitingOrderPutResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingOrderPutResponse = action.payload;
        },
        setIsWaitingOrderGetResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingOrderGetResponse = action.payload;
        },
        setIsWaitingExportedNotImportedOrdersGetResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingExportedNotImportedOrdersGetResponse = action.payload;
        },
        setIsWaitingOrderExportResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingOrderExportResponse = action.payload;
        },
        setIsWaitingStockResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingStockResponse = action.payload;
        },
        setArticleStockValues: (state, action: PayloadAction<Article[]>) => {
            if (state.order.articles) {
                state.order.articles = state.order.articles.map(article => {
                    const incomingArticle = action.payload.find(a => a.id === article.id);

                    let salesPrice = article.salesPrice;
                    if (incomingArticle?.salesPrice)
                        salesPrice = parseFloat(interpretAsDecimal(parseFloat(incomingArticle.salesPrice)).toFixed(2)).toString();

                    return {
                        ...article,
                        stock: incomingArticle?.stock || 0,
                        adId: incomingArticle?.adId || article.adId,
                        salesPrice: salesPrice,
                        discount:
                            incomingArticle?.discount != null && incomingArticle.discount !== 0
                                ? incomingArticle.discount
                                : article.discount,
                    };
                });
            }
        },
        setIsWaitingRevertOrderExportResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingOrderExportResponse = action.payload;
        },
        setImportedArticlesCount: (state, action: PayloadAction<number>) => {
            state.importedArticlesCount = action.payload;
        },
        setQueryOrderList: (state, action: PayloadAction<{
            query: QueryOrder;
        }>) => {
            state.queryOrderList = action.payload.query.orders || initialState.queryOrderList;
            state.totalQueryOrder = action.payload.query.totalCount || initialState.totalQueryOrder;
            return state;
        },
        setIsWaitingOrderHistoryGetResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingOrderHistoryGetResponse = action.payload;
        },
        setOrderHistoryList: (state, action: PayloadAction<{ history: OrderHistory[] }>) => {
            state.order.orderHistory = action.payload.history;
        },
        setPdfVerification: (state, action: PayloadAction<Blob | undefined>) => {
            state.pdfVerification = action.payload;
        },
        setEmailHistory: (state, action: PayloadAction<EmailHistory[] | null>) => {
            state.emailHistory = action.payload;
        },
        setIsWaitingSendOrderEmailResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingSendOrderEmailResponse = action.payload;
        },
        setHasUpdatedStock: (state, action: PayloadAction<boolean>) => {
            state.hasUpdatedStock = action.payload;
        },
        setIsWaitingPriceProposalPreviewResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingPriceProposalPreviewResponse = action.payload;
        },
        setIsWaitingOrderStatusSyncResponse: (state, action: PayloadAction<boolean>) => {
            state.isWaitingOrderStatusSyncResponse = action.payload;
        }   
    },
});

export const {
    setOrderList,
    setOrder,
    setIsWaitingOrderPostResponse,
    setIsWaitingOrderPutResponse,
    setIsWaitingOrderGetResponse,
    setIsWaitingExportedNotImportedOrdersGetResponse,
    setImportedArticlesCount,
    setQueryOrderList,
    setOrderUser,
    setOrderListByUserId,
    setExportedNotImportedOrders,
    setIsWaitingOrderExportResponse,
    setIsWaitingRevertOrderExportResponse,
    setIsWaitingStockResponse,
    setIsWaitingOrderHistoryGetResponse,
    setOrderHistoryList,
    setPdfVerification,
    setEmailHistory,
    setIsWaitingSendOrderEmailResponse,
    setArticleStockValues,
    setHasUpdatedStock,
    setIsWaitingPriceProposalPreviewResponse,
    setIsWaitingOrderStatusSyncResponse
} = orderSlice.actions;

export default orderSlice.reducer;